import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { httpGet, httpPost } from "../helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import { formatDay, todayIso } from "./parseIssueQuery";
import "./archive.css";

const MAX = 10;
const FIRST_YEAR = 1960;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SPANS = [
  { id: "day", label: "Exact dates" },
  { id: "month", label: "A month" },
  { id: "year", label: "A year" },
];
// Plain words for what is being asked: "Thu, 12 Mar 1987", "March 1987", "Any issue from 1987".
export const periodLabel = (span, d) => {
  const [y, m] = String(d).split("-").map(Number);
  if (span === "year") return `Any issue from ${y}`;
  if (span === "month") return `${MONTHS[m - 1]} ${y}`;
  return formatDay(d);
};

const Check = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

export default function RequestIssueModal({ onClose, onSent, history, initialPublication, initialDates, initialPaperName, initialSpan }) {
  const { userIsLogin } = useContext(UserDataContext);
  const [step, setStep] = useState("form");
  const [pub, setPub] = useState(initialPublication || null);
  const [notListed, setNotListed] = useState(Boolean(initialPaperName) && !initialPublication);
  const [paperName, setPaperName] = useState(initialPaperName || "");
  const [pubQuery, setPubQuery] = useState("");
  const [pubResults, setPubResults] = useState([]);
  const [dates, setDates] = useState((initialDates || []).filter(Boolean).slice(0, MAX));
  const [dateInput, setDateInput] = useState("");
  // Readers who don't know the exact day can ask for a whole month or year ("any issue from 1987").
  const [span, setSpan] = useState(["month", "year"].includes(initialSpan) ? initialSpan : "day");
  const thisYear = Number(todayIso().slice(0, 4));
  const [pickYear, setPickYear] = useState(String(thisYear - 20));
  const [pickMonth, setPickMonth] = useState("1");
  const [note, setNote] = useState("");
  const [available, setAvailable] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Search publications on TNS.
  useEffect(() => {
    clearTimeout(timer.current);
    if (pub || notListed || pubQuery.trim().length < 2) {
      setPubResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      const r = await httpGet(`search?q=${encodeURIComponent(pubQuery.trim())}&type=publications&limit=6`);
      setPubResults(r && r.data && Array.isArray(r.data.publications) ? r.data.publications : []);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [pubQuery, pub, notListed]);

  // Dates that are already on TNS are shown green and not requested.
  useEffect(() => {
    if (!pub || !dates.length || span !== "day") {
      setAvailable({});
      return undefined;
    }
    let live = true;
    httpGet(`archive_lookup?publicationId=${pub.id}&dates=${dates.join(",")}`).then((r) => {
      if (!live || !r || !r.data || !Array.isArray(r.data.results)) return;
      const map = {};
      r.data.results.forEach((x) => {
        if (x.edition && ["on_tns", "archive", "owned"].includes(x.status)) map[x.date] = x.edition.id;
      });
      setAvailable(map);
    });
    return () => {
      live = false;
    };
  }, [pub, dates, span]);

  const changeSpan = (next) => {
    if (next === span) return;
    setSpan(next);
    setDates([]);
    setError("");
  };

  const addDate = () => {
    const d = span === "year" ? `${pickYear}-01-01` : span === "month" ? `${pickYear}-${String(pickMonth).padStart(2, "0")}-01` : dateInput;
    if (span === "month" && d.slice(0, 7) > todayIso().slice(0, 7)) return setError("Pick a month that has already passed.");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return setError("Pick a date first.");
    if (d > todayIso()) return setError("Pick a date that has already passed.");
    if (d < "1960-01-01") return setError("Requests go back to 1960.");
    if (dates.includes(d)) return setDateInput("");
    if (dates.length >= MAX) return setError(`You can request up to ${MAX} dates at a time.`);
    setError("");
    setDates([...dates, d].sort());
    return setDateInput("");
  };

  const toRequest = dates.filter((d) => !available[d]);
  const paperOk = notListed ? paperName.trim().length >= 2 : Boolean(pub);
  const canSend = paperOk && toRequest.length > 0 && !busy;
  const paperLabel = notListed ? paperName.trim() : pub ? pub.name : "";
  const availableDates = Object.keys(available);

  const goLogin = () => {
    onClose();
    history.push("/login");
  };

  const send = async () => {
    if (!canSend) return;
    if (userIsLogin !== true) {
      goLogin();
      return;
    }
    setBusy(true);
    setError("");
    const body = { dates: toRequest, span, note: note.trim() || undefined };
    if (notListed) body.paperName = paperName.trim();
    else body.publicationId = pub.id;
    const r = await httpPost("archive_requests", body);
    setBusy(false);
    if (!r || r.er) {
      setError((r && r.er && (r.er.message || String(r.er))) || "Something went wrong. Please try again.");
      return;
    }
    setResult(r.data);
    setStep("sent");
    if (onSent) onSent(r.data);
  };

  const others =
    result && Array.isArray(result.others) && result.others.length ? Math.max(...result.others.map((o) => o.otherReaders)) : 0;

  const sent = result ? (
    <>
      <span className="ar-success-icon"><Check /></span>
      <div>
        <h2 id="ar-modal-title" className="ar-title">Request sent</h2>
        <p className="ar-sub">
          {result.requested.length ? (
            <>We’ve asked {paperLabel} for {result.requested.map((d) => periodLabel(result.span || span, d).replace(/^Any/, "any")).join(", ")}.</>
          ) : (
            <>There was nothing new to request.</>
          )}
        </p>
      </div>
      {result.alreadyAvailable && result.alreadyAvailable.length && result.publication ? (
        <p className="ar-info">
          {result.alreadyAvailable.map((a) => periodLabel(result.span || span, a.date)).join(", ")} {result.alreadyAvailable.length === 1 ? "is" : "are"} already on TNS.{" "}
          <button type="button" className="ar-link" onClick={() => { onClose(); history.push(`/archive?paper=${result.publication.id}&month=${result.alreadyAvailable[0].date.slice(0, 7)}`); }}>See {result.alreadyAvailable.length === 1 ? "it" : "them"}</button>
        </p>
      ) : null}
      {result.alreadyRequested > 0 ? (
        <p className="ar-info">You had already requested {result.alreadyRequested} of these. We’ve kept those as they were.</p>
      ) : null}
      {others > 0 ? (
        <p className="ar-info">{others} other reader{others === 1 ? " has" : "s have"} asked for this too, so it’s higher on the list.</p>
      ) : null}
      <ol className="ar-steps">
        <li className="ar-step">
          <span className="ar-step-n is-done">1</span>
          <span>
            <strong>{result.publication ? "Publisher notified" : "Our team is on it"}</strong>
            {result.publication ? `${paperLabel} will see your request in their TNS dashboard.` : `We’ll look for ${paperLabel} and try to bring it to TNS.`}
          </span>
        </li>
        <li className="ar-step">
          <span className="ar-step-n">2</span>
          <span><strong>The issue is added</strong>If it can’t be found, we’ll let you know.</span>
        </li>
        <li className="ar-step">
          <span className="ar-step-n">3</span>
          <span><strong>We email you</strong>Pay the archive price and read it straight away. It stays in your library.</span>
        </li>
      </ol>
      <div className="ar-actions">
        <button type="button" className="ar-btn ar-btn-outline" onClick={onClose}>Done</button>
        <button type="button" className="ar-btn" onClick={() => { onClose(); history.push("/archive#requests"); }}>View my requests</button>
      </div>
    </>
  ) : null;

  const form = (
    <>
      <div className="ar-modal-head">
        <div>
          <h2 id="ar-modal-title" className="ar-title">Request an issue</h2>
          <p className="ar-sub">Tell us the paper and dates. We’ll ask the publisher and email you when each one is ready.</p>
        </div>
        <button type="button" className="ar-close" aria-label="Close" onClick={onClose}>✕</button>
      </div>

      <div className="ar-field">
        <span className="ar-label">Paper or magazine</span>
        {notListed ? (
          <>
            <input className="ar-input" value={paperName} onChange={(e) => setPaperName(e.target.value)} placeholder="Name of the paper" aria-label="Name of the paper" maxLength={120} autoFocus />
            <button type="button" className="ar-link" onClick={() => setNotListed(false)}>Search papers on TNS instead</button>
          </>
        ) : pub ? (
          <div className="ar-picked">
            <span className="ar-logo">{pub.logo ? <img src={pub.logo} alt="" /> : (pub.name || "?").charAt(0)}</span>
            <span className="ar-picked-name">{pub.name}</span>
            <button type="button" className="ar-chip-btn" onClick={() => { setPub(null); setPubQuery(""); }}>Change</button>
          </div>
        ) : (
          <>
            <input className="ar-input" value={pubQuery} onChange={(e) => setPubQuery(e.target.value)} placeholder="Search for a paper, e.g. Punch" aria-label="Search for a paper" autoFocus />
            {pubResults.length > 0 ? (
              <ul className="ar-options">
                {pubResults.map((p) => (
                  <li key={p.id}>
                    <button type="button" className="ar-option" onClick={() => { setPub({ id: p.id, name: (p.name || "").trim(), logo: p.logo }); setPubResults([]); }}>
                      <span className="ar-logo ar-logo-sm">{p.logo ? <img src={p.logo} alt="" /> : (p.name || "?").charAt(0)}</span>
                      {(p.name || "").trim()}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <button type="button" className="ar-link" onClick={() => { setNotListed(true); setPaperName(pubQuery); }}>Paper not on TNS? Tell us its name</button>
          </>
        )}
      </div>

      <div className="ar-field">
        <span className="ar-label">What do you need? <span className="ar-muted">(up to {MAX})</span></span>
        <div className="ar-seg" role="radiogroup" aria-label="Request by">
          {SPANS.map((x) => (
            <button key={x.id} type="button" role="radio" aria-checked={span === x.id} className={span === x.id ? "on" : ""} onClick={() => changeSpan(x.id)}>{x.label}</button>
          ))}
        </div>
        {span !== "day" ? <p className="ar-hint">Not sure of the day? We’ll look for any issue from that {span}, and email you when there is one.</p> : null}
        {dates.length > 0 ? (
          <div className="ar-chips">
            {dates.map((d) => (
              <span key={d} className={`ar-chip${available[d] ? " is-available" : ""}`}>
                {periodLabel(span, d)}{available[d] ? " · on TNS" : ""}
                <button type="button" aria-label={`Remove ${periodLabel(span, d)}`} onClick={() => setDates(dates.filter((x) => x !== d))}>✕</button>
              </span>
            ))}
          </div>
        ) : null}
        <div className="ar-row">
          {span === "day" ? (
            <input type="date" className="ar-input" min="1960-01-01" max={todayIso()} value={dateInput} onChange={(e) => setDateInput(e.target.value)} aria-label="Add a date" />
          ) : (
            <div className="ar-pick">
              {span === "month" ? (
                <select className="ar-input" value={pickMonth} onChange={(e) => setPickMonth(e.target.value)} aria-label="Month">
                  {MONTHS.map((mName, i) => <option key={mName} value={i + 1}>{mName}</option>)}
                </select>
              ) : null}
              <select className="ar-input" value={pickYear} onChange={(e) => setPickYear(e.target.value)} aria-label="Year">
                {Array.from({ length: thisYear - FIRST_YEAR + 1 }, (_, i) => String(thisYear - i)).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          <button type="button" className="ar-btn ar-btn-outline" onClick={addDate}>+ Add</button>
        </div>
        {availableDates.length > 0 && pub ? (
          <p className="ar-info">
            {availableDates.length === 1 ? `${formatDay(availableDates[0])} is` : `${availableDates.length} of these dates are`} already on TNS, so{" "}
            {availableDates.length === 1 ? "it won’t" : "they won’t"} be requested.{" "}
            <button type="button" className="ar-link" onClick={() => { onClose(); history.push(`/publication/${encodeURIComponent(pub.name)}`); }}>Open {pub.name}</button>
          </p>
        ) : null}
      </div>

      <div className="ar-field">
        <label className="ar-label" htmlFor="ar-note">What are you looking for? <span className="ar-muted">(optional)</span></label>
        <textarea id="ar-note" className="ar-input ar-textarea" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. a story on the budget, around page 5" />
      </div>

      <div className="ar-promise"><strong>Free to request.</strong> You only pay the archive price when an issue is ready, and we’ll email you.</div>
      {error ? <p className="ar-error" role="alert">{error}</p> : null}
      <div className="ar-actions">
        <button type="button" className="ar-btn ar-btn-outline" onClick={onClose}>Cancel</button>
        <button type="button" className="ar-btn" disabled={!canSend} onClick={send}>
          {busy ? "Sending…" : userIsLogin !== true ? "Log in to send request" : toRequest.length > 1 ? `Send request for ${toRequest.length} issues` : "Send request"}
        </button>
      </div>
    </>
  );

  return createPortal(
    <div className="ar-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ar-modal" role="dialog" aria-modal="true" aria-labelledby="ar-modal-title">
        {step === "sent" ? sent : form}
      </div>
    </div>,
    document.body
  );
}